/**
 * Which currency a checkout opens in.
 *
 * The table of flags and symbols that used to live here is gone: the currency control is ISO
 * codes and words (TECH.md, "Language and currency are words, not flags"), and every symbol the
 * app needs comes from Intl through src/utils/money.ts. What is left is the resolution order,
 * which is unchanged: what the reader already chose in this session, then the URL, then the
 * browser's own locale, then the coach's default.
 */

// Locale → currency mapping
const LOCALE_CURRENCY_MAP: Record<string, string> = {
  "en-us": "usd",
  "en-gb": "gbp",
  "en-au": "aud",
  "en-ca": "cad",
  "fr-fr": "eur",
  "fr-be": "eur",
  "fr-ch": "chf",
  "fr-ca": "cad",
  "de-de": "eur",
  "de-at": "eur",
  "de-ch": "chf",
  "es-es": "eur",
  "es-mx": "mxn",
  "pt-br": "brl",
  "pt-pt": "eur",
  "it-it": "eur",
  "it-ch": "chf",
};

/**
 * Detect the user's preferred currency from browser locale.
 */
export function detectPreferredCurrency(): string | null {
  try {
    // Try navigator.language first
    const lang = navigator.language?.toLowerCase();
    if (lang && LOCALE_CURRENCY_MAP[lang]) {
      return LOCALE_CURRENCY_MAP[lang];
    }

    // Try Intl.DateTimeFormat resolved locale
    const resolved = Intl.DateTimeFormat()
      .resolvedOptions()
      .locale?.toLowerCase();
    if (resolved && LOCALE_CURRENCY_MAP[resolved]) {
      return LOCALE_CURRENCY_MAP[resolved];
    }

    // Fallback: try just the language part (e.g. "en" → "usd")
    const langShort = lang?.split("-")[0];
    if (langShort === "en") return "usd";
    if (langShort === "fr") return "eur";
    if (langShort === "de") return "eur";
    if (langShort === "es") return "eur";
    if (langShort === "pt") return "eur";
    if (langShort === "it") return "eur";

    return null;
  } catch {
    return null;
  }
}

/**
 * Resolve the best currency to display at checkout.
 * Priority: sessionStorage > URL param > detected > default
 */
export function resolveCheckoutCurrency(
  availableCurrencies: string[],
  defaultCurrency: string,
  preferred: string | null,
): string {
  if (availableCurrencies.length <= 1) {
    return defaultCurrency;
  }

  // Check sessionStorage first (for 3DS redirect recovery)
  try {
    const stored = sessionStorage.getItem("gymkee_checkout_currency");
    if (stored && availableCurrencies.includes(stored)) {
      return stored;
    }
  } catch {
    // sessionStorage not available
  }

  // Check URL param
  const urlParams = new URLSearchParams(window.location.search);
  const urlCurrency = urlParams.get("currency")?.toLowerCase();
  if (urlCurrency && availableCurrencies.includes(urlCurrency)) {
    return urlCurrency;
  }

  // Use preferred (detected from browser)
  if (preferred && availableCurrencies.includes(preferred)) {
    return preferred;
  }

  // Fallback to default
  return defaultCurrency;
}
